import React, {useState, useEffect, useMemo, useCallback} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, Table, Loading} from '../../../components'
import {CustomCell} from '../../../components/Table/columns/CustomCell'
import {CustomHeader} from '../../../components/Table/columns/CustomHeader'
import {ListHeader} from '../../../components/Table/header/ListHeader'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {contractsGetAll, contractsExcel} from '../../../redux/slices/project.slice'
import {projectLookup, allCompanyLookup, basicLookup} from '../../../redux/slices/lookup.slice'

import {
  useMemoizedStaticData
} from '../../../utils/performanceOptimizations'

const selectContractsData = (state) => ({
  contracts: state.project.contracts,
  loading: state.project.loading
})

const selectLookupData = (state) => ({
  projectLookupList: state.lookup.projectLookupList,
  companyLookup: state.lookup.companyLookup,
  ContractType: state.lookup.ContractType,
  ContractState: state.lookup.ContractState
})

function List() {
  const intl = useIntl()
  const {contracts, loading} = useSelector(selectContractsData)
  const {projectLookupList, companyLookup, ContractType, ContractState} = useSelector(selectLookupData)
  const dispatch = useDispatch()

  const [params, setParams] = useState()

  useEffect(() => {
    if (params) dispatch(contractsGetAll(params))
  }, [dispatch, params])

  useEffect(() => {
    dispatch(projectLookup())
    dispatch(allCompanyLookup())
    dispatch(basicLookup('ContractType'))
    dispatch(basicLookup('ContractState'))
  }, [dispatch])

  const handleSorting = useCallback((item) => {
    setParams((prevState) => ({...prevState, sort: item && item.sort, order: item && item.order}))
  }, [])

  const handleSearch = useCallback((item) => {
    setParams((prevState) => ({...prevState, name: item}))
  }, [])

  const handlePagination = useCallback((pagination) => {
    setParams((prevState) => ({...prevState, ...pagination}))
  }, [])

  const handleExcelExport = useCallback(() => {
    dispatch(
      contractsExcel({
        params,
        fileName: intl.formatMessage({id: 'BC.CONTRACTS'}),
      })
    )
  }, [dispatch, params, intl])

  const handleFilterSubmit = useCallback((values) => {
    setParams({...values})
  }, [])

  const columns = useMemo(() => [
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'CONTRACT.SEARCH.PROJECTID'})}
          className='min-w-125px'
        />
      ),
      accessor: 'projectName',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'CONTRACT.SEARCH.CONTRACTTYPEID'})}
          className='min-w-125px'
        />
      ),
      accessor: 'contractTypeName',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'CONTRACT.SEARCH.CONTRACTSTATEID'})}
          className='min-w-125px'
        />
      ),
      accessor: 'contractStateName',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'CONTRACT.SEARCH.NAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'name',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'CONTRACT.SEARCH.COMPANYID'})}
          className='min-w-125px'
        />
      ),
      accessor: 'companyName',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'CONTRACT.SEARCH.STARTDATE'})}
          className='min-w-125px'
        />
      ),
      accessor: 'startDate',
      Cell: ({...props}) => <CustomCell item={props} date='short' />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'CONTRACT.SEARCH.ENDDATE'})}
          className='min-w-125px'
        />
      ),
      accessor: 'endDate',
      Cell: ({...props}) => <CustomCell item={props} date='short' />,
    },
  ], [intl, handleSorting])

  const actionButtons = useMemoizedStaticData(() => [
    {
      title: 'BUTTON.EXCEL',
      onClick: handleExcelExport,
      icon: '/images/icons/duotune/arrows/arr044.svg',
      variant: 'light-primary',
    },
    {
      title: 'FILTER',
      icon: '/images/icons/duotune/general/gen031.svg',
      variant: 'light-primary',
      list: [
        {
          title: 'CONTRACT.SEARCH.PROJECTID',
          data: projectLookupList,
          name: 'projectId',
        },
        {
          title: 'CONTRACT.SEARCH.COMPANYID',
          data: companyLookup,
          name: 'companyId',
        },
        {
          title: 'CONTRACT.SEARCH.CONTRACTTYPEID',
          data: ContractType,
          name: 'contractTypeId',
        },
        {
          title: 'CONTRACT.SEARCH.CONTRACTSTATEID',
          data: ContractState,
          name: 'contractStateId',
        },
        {
          title: 'CONTRACT.SEARCH.NAME',
          type: 'text',
          name: 'contractName',
        },
        {
          title: 'CONTRACT.SEARCH.STARTDATE',
          type: 'date',
          name: 'startDate',
        },
        {
          title: 'CONTRACT.SEARCH.ENDDATE',
          type: 'date',
          name: 'endDate',
        },
      ],
    },
  ], [handleExcelExport, projectLookupList, companyLookup, ContractType, ContractState])

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'BC.CONTRACTS'})}</PageTitle>
      <KTCard>
        <ListHeader
          search={handleSearch}
          actionButtons={actionButtons}
          onSubmit={handleFilterSubmit}
          pages={'CONTRACTS'}
        />
        <KTCardBody>
          {!contracts && <ListLoading />}
          {contracts && (
            <Table
              items={contracts}
              cols={columns}
              handlePagination={handlePagination}
              paddless
            />
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {List}
